import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  useTheme,
  Typography,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import { CREATE_EXTERNAL_USER } from "../../../../apis/organization-apis/people-apis/externalUserApis";
import { GET_ALL_ROLES } from "../../../../apis/dashboard-apis/dashboardDetailApi";
import Role from "../../../../utils/roles";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import DatePicker from "../../../../components/DatePicker/DatePicker";
import { useToday } from "../../../../utils/dateUtils";

const AddExternalUserDialog = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const today = useToday();
  const [createExternalUser, { loading }] = useMutation(CREATE_EXTERNAL_USER);
  const { data: rolesData } = useQuery(GET_ALL_ROLES);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role_id: "",
    access_from: "",
    access_till: "",
  });
  const [errors, setErrors] = useState({});
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  
  useEffect(() => {
    if (open && rolesData?.getAllRoles) {
      const normalizedRoles = rolesData.getAllRoles.filter((role) => role.is_active);
      const auditorRole = normalizedRoles.find(
        (role) =>
          role.role_name?.toLowerCase() === Role.AUDITOR.toLowerCase()
      );
      if (auditorRole) {
        setFormData((prev) => ({
          ...prev,
          role_id: auditorRole.role_id.toString(),
        }));
      }
    }
  }, [open, rolesData]);

  // Filter roles to only show Auditor for external users
  const availableRoles = useMemo(() => {
    if (!rolesData?.getAllRoles) return [];

    const allowedRoles = [Role.AUDITOR.toLowerCase()];

    const filtered = rolesData.getAllRoles.filter((role) => {
      if (!role.is_active) return false;
      const roleName = (role.role_name || "").toLowerCase().trim();
      // Match by role name (case-insensitive, exact or partial match)
      return allowedRoles.some((allowedRole) => 
        roleName === allowedRole || roleName.includes(allowedRole)
      );
    });

    // Sort: Auditor only
    const sortOrder = {
      [Role.AUDITOR.toLowerCase()]: 1,
    };
    return filtered.sort((a, b) => {
      const aName = (a.role_name || "").toLowerCase().trim();
      const bName = (b.role_name || "").toLowerCase().trim();
      const aOrder = Object.keys(sortOrder).find((key) => 
        aName === key || aName.includes(key)
      ) || "";
      const bOrder = Object.keys(sortOrder).find((key) => 
        bName === key || bName.includes(key)
      ) || "";
      return (sortOrder[aOrder] || 999) - (sortOrder[bOrder] || 999);
    });
  }, [rolesData]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.role_id) {
      newErrors.role_id = "Role is required";
    }
    if (!formData.access_from) {
      newErrors.access_from = "Access From date is required";
    }
    if (!formData.access_till) {
      newErrors.access_till = "Access Till date is required";
    }
    if (formData.access_from && formData.access_till) {
      const fromDate = new Date(formData.access_from);
      const tillDate = new Date(formData.access_till);
      if (tillDate < fromDate) {
        newErrors.access_till = "Access end date must be greater than or equal to start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const response = await createExternalUser({
        variables: {
          input: {
            name: formData.name.trim(),
            email: formData.email.trim(),
            role_id: parseInt(formData.role_id),
            access_from: formData.access_from || null,
            access_till: formData.access_till || null,
          },
        },
      });

      if (response?.data?.createExternalUser?.success) {
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else {
        console.error(
          "Failed to create external user:",
          response?.data?.createExternalUser?.message
        );
        setSnackbarInfo({
          message:
            response?.data?.createExternalUser?.message ||
            "Failed to create external user",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      console.error("Error creating external user:", error);
      setSnackbarInfo({
        message:
          error.message || "An error occurred while creating external user",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleClose = () => {
    const normalizedRoles = rolesData?.getAllRoles?.filter((role) => role.is_active) || [];
    const auditorRole = normalizedRoles.find(
      (role) =>
        role.role_name?.toLowerCase() === Role.AUDITOR.toLowerCase()
    );
    setFormData({
      name: "",
      email: "",
      role_id: auditorRole ? auditorRole.role_id.toString() : "",
      access_from: "",
      access_till: "",
    });
    setErrors({});
    setSnackbarInfo(null);
    onClose();
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "visible",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Typography variant="h6" fontWeight={600} color={theme.customColors.text.strong}>
          Add External User
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: theme.customColors.text.secondary }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          background: theme.customColors.background.main,
          pt: 3,
        }}
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: theme.customColors?.text?.strong ?? "#374151",
                mb: 0.75,
              }}
            >
              Name *
            </Typography>
            <TextField
              fullWidth
              placeholder="Name"
              value={formData.name}
              onChange={handleChange("name")}
              error={!!errors.name}
              helperText={errors.name}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                  "&:hover fieldset": {
                    borderColor: theme.customColors.border.strong,
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: theme.customColors?.text?.strong ?? "#374151",
                mb: 0.75,
              }}
            >
              Email *
            </Typography>
            <TextField
              fullWidth
              placeholder="Email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
              error={!!errors.email}
              helperText={errors.email}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                  "&:hover fieldset": {
                    borderColor: theme.customColors.border.strong,
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: theme.customColors?.text?.strong ?? "#374151",
                mb: 0.75,
              }}
            >
              Role *
            </Typography>
            <FormControl fullWidth error={!!errors.role_id}>
              <Select
                value={formData.role_id}
                onChange={handleChange("role_id")}
                displayEmpty
                MenuProps={{
                  disableScrollLock: true,
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                    sx: {
                      "& .MuiMenuItem-root": {
                        minHeight: "48px",
                      },
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.customColors.border.strong,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: errors.role_id
                      ? theme.palette.error.main
                      : theme.customColors.border.main,
                  },
                }}
              >
                {availableRoles.length === 0 ? (
                  <MenuItem disabled>Loading roles...</MenuItem>
                ) : (
                  availableRoles.map((role) => (
                    <MenuItem 
                      key={role.role_id} 
                      value={role.role_id}
                      sx={{
                        py: 1.5,
                      }}
                    >
                      {role.role_name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.role_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.role_id}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              fullWidth
              label="Access From"
              required
              value={formData.access_from}
              onChange={(date) => handleChange("access_from")({ target: { value: date } })}
              error={!!errors.access_from}
              helperText={errors.access_from || "Start date of access"}
              minDate={today}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              fullWidth
              label="Access Till"
              required
              value={formData.access_till}
              onChange={(date) => handleChange("access_till")({ target: { value: date } })}
              error={!!errors.access_till}
              helperText={errors.access_till || "End date of access"}
              minDate={formData.access_from || today}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          pb: 2.5,
          pt: 2,
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            borderColor: theme.customColors.border.main,
            color: theme.customColors.text.strong,
            "&:hover": {
              borderColor: theme.customColors.border.strong,
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={loading}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            bgcolor: theme.customColors.button.hover,
            color: theme.customColors.button.text,
            "&:hover": {
              bgcolor: theme.customColors.button.primary,
            },
            "&:disabled": {
              bgcolor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          Add External User
        </LoadingButton>
      </DialogActions>
    </Dialog>
    <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default AddExternalUserDialog;
